import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  Validators,
} from '@angular/forms';

import {
  METRO_CARD_NUMBER_SECTION_LIST, METRO_CARD_NUMBER_DELIMITER
} from '../../constants/metro-card-field.constants';
import { MetroCardUtil } from '../../utils/metro-card.util';

@Component({
  selector: 'app-metro-card-field',
  templateUrl: './metro-card-field.component.html',
  styleUrls: ['./metro-card-field.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MetroCardFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MetroCardFieldComponent),
      multi: true
    },
  ]
})
export class MetroCardFieldComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  public cardNumberForm: FormGroup;
  public readonly cardNumberSections = METRO_CARD_NUMBER_SECTION_LIST;

  private unsubscribe$ = new Subject<boolean>();

  constructor(private formBuilder: FormBuilder) {
  }

  public ngOnInit(): void {
    this.createForm();

    this.cardNumberForm.valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
      )
      .subscribe(this.onCardNumberChange.bind(this));
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(false);
    this.unsubscribe$.complete();
  }

  public onChange = (_: any) => {};

  public onTouched = () => {};

  public writeValue(cardNumber: string): void {
    const [store = '', customer = '', cardholder = '', service = ''] = cardNumber.split(METRO_CARD_NUMBER_DELIMITER);
    this.cardNumberForm.patchValue({ store, customer, cardholder, service });
  }

  public registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public validate() {
    if (this.cardNumberForm.valid) {
      return null;
    }

    return { cardNumber: true };
  }

  public onPaste(event): void {
    const clipboardDigitList = event.clipboardData.getData('text').match(/\d+/g);
    const cardNumber = clipboardDigitList ? clipboardDigitList.join('') : '';
    const [storeValue, customerValue, cardholderValue, serviceValue] = MetroCardUtil.parseMetroCardNumber(cardNumber);

    const { store, customer, cardholder, service } = this.cardNumberForm.controls;
    store.setValue(storeValue);
    customer.setValue(customerValue);
    cardholder.setValue(cardholderValue);
    service.setValue(serviceValue);
  }

  private createForm() {
    const { required, maxLength, minLength } = Validators;

    const formControls = this.cardNumberSections.reduce((acc, section)  => {
      const validators = [required, minLength(section.minLength), maxLength(section.maxLength)];

      return {
        ...acc,
        [section.name]: ['', { validators }],
      };
    }, {});

    this.cardNumberForm = this.formBuilder.group(formControls);
  }

  private onCardNumberChange(values) {
    const { store, customer, cardholder, service } = values;
    const cardNumber = [store, customer, cardholder, service].join(METRO_CARD_NUMBER_DELIMITER);

    this.onChange(cardNumber);
  }
}

